import { AUTHORITIES } from "@/config/constants";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { hasAnyAuthority } from "@/shared/auth/private-route";
import { IEdoVideostorePost } from "@/shared/model/edo-videostore-post.model";
import { IEdoVideoStatusEnum } from "@/shared/model/enumerations/edo-video-status.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { publishVideostorePost } from "@/shared/reducers/entities/edo-videostore-post.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import {
  ITEMS_PER_PAGE,
  ITEMS_PER_PAGE_OPTIONS,
} from "@/shared/util/pagination.constants";
import {
  Avatar,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Pagination,
} from "@/components/ui";
import { Loader2, Pencil, Trash2, Upload, Video } from "lucide-react";
import axios from "axios";
import { SocketContext } from "context/socket";
import { useContext, useEffect, useState } from "react";
import { IPaginationBaseState, Translate } from "react-jhipster";
import { getVideoStatusLabel } from "../videostore";

interface IVideostoreDraftsProps {
  open: boolean;
  onClose: () => void;
  onEdit: (post: IEdoVideostorePost) => void;
  onDelete: (post: IEdoVideostorePost) => void;
}

const VideostoreDrafts = (props: IVideostoreDraftsProps) => {
  const dispatch = useAppDispatch();
  const socket = useContext(SocketContext);
  const { updateSuccess } = useAppSelector<EntityState<IEdoVideostorePost>>(
    (state) => state.edoVideostorePost
  );
  const [videostorePosts, setVideostorePosts] = useState<{
    loading: boolean;
    entities: IEdoVideostorePost[];
    totalItems: number;
  }>({ loading: false, entities: [], totalItems: 0 });
  const { account } = useAppSelector<AuthenticationState>((state) => state.authentication);
  const [paginationState, setPaginationState] = useState<IPaginationBaseState>({
    activePage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
    sort: "updatedAt",
    order: "ASC",
  });
  const { open, onClose: handleClose, onEdit: handleEdit, onDelete: handleDelete } = props;

  const getVideostorePostList = async () => {
    if (!account?.id) return;
    setVideostorePosts((prev) => ({ ...prev, loading: true }));
    const response = await axios.get<{
      data: IEdoVideostorePost[];
      meta: { totalItems: number };
      links: unknown;
    }>("videostore-posts?status.equals=DRAFT", {
      params: {
        page: paginationState.activePage,
        limit: paginationState.itemsPerPage,
        sortBy: `${paginationState.sort}:${paginationState.order}`,
        "filter.author": `$eq:${account.id}`,
        "filter.draft": "$eq:1",
      },
    });
    setVideostorePosts({
      entities: response.data.data,
      loading: false,
      totalItems: response.data.meta?.totalItems ?? 0,
    });
  };

  const handlePagination = (page: number) =>
    setPaginationState({ ...paginationState, activePage: page + 1 });

  const handleRowsPerPage = (rowsPerPage: number) =>
    setPaginationState({
      ...paginationState,
      itemsPerPage: rowsPerPage,
      activePage: 1,
    });

  const handlePublish = async (videostorePost: IEdoVideostorePost) => {
    if (!videostorePost.id) return;
    dispatch(publishVideostorePost(videostorePost.id));
  };

  const handleOnVideoUpdate = () => {
    getVideostorePostList();
  };

  useEffect(() => {
    if (open && account?.id) {
      getVideostorePostList();
    }
  }, [
    open,
    account?.id,
    paginationState.activePage,
    paginationState.order,
    paginationState.sort,
    paginationState.itemsPerPage,
  ]);

  useEffect(() => {
    if (account?.id) {
      socket.on(`user:${account.id}:video-update`, handleOnVideoUpdate);
    }
    return () => {
      if (account?.id) {
        socket.off(`user:${account.id}:video-update`, handleOnVideoUpdate);
      }
    };
  }, [account]);

  useEffect(() => {
    if (updateSuccess && open) {
      getVideostorePostList();
    }
  }, [updateSuccess]);

  const isAdmin = hasAnyAuthority(account?.roles ?? [], [AUTHORITIES.ROLE_ADMIN]);

  if (!isAdmin) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden p-0">
        <DialogHeader className="flex-shrink-0 border-b border-[var(--color-mockup-border)] px-5 py-4">
          <DialogTitle className="text-lg">Bozze videoteca</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {videostorePosts.loading ? (
            <div className="flex justify-center py-12">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : videostorePosts.entities?.length > 0 ? (
            <List className="divide-y divide-[var(--color-mockup-border)]">
              {videostorePosts.entities.map((videostorePost) => (
                <ListItem
                  key={videostorePost.id}
                  className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-primary/5"
                >
                  <ListItemIcon>
                    <Avatar className="h-10 w-10 rounded-2xl bg-primary/10 text-primary">
                      <Video className="h-5 w-5" />
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <span className="text-sm font-semibold text-[var(--color-mockup-text)]">
                        {videostorePost.title || "Post senza titolo"}
                      </span>
                    }
                    secondary={
                      <span className="text-xs text-[var(--color-mockup-text-light)]">
                        {getVideoStatusLabel(videostorePost?.video?.status)}
                      </span>
                    }
                  />
                  <div className="ml-auto flex items-center gap-1">
                    {videostorePost.video?.status === IEdoVideoStatusEnum.READY && (
                      <button
                        type="button"
                        onClick={() => handlePublish(videostorePost)}
                        disabled={
                          videostorePosts.loading ||
                          !videostorePost.title ||
                          !videostorePost.subject ||
                          !videostorePost.schools?.length ||
                          !videostorePost.years?.length ||
                          !videostorePost.draft ||
                          videostorePost?.video?.status !== IEdoVideoStatusEnum.READY
                        }
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-mockup-text-light)] transition-colors hover:bg-primary/10 hover:text-primary"
                        aria-label="Pubblica"
                      >
                        <Upload className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleEdit(videostorePost)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-mockup-text-light)] transition-colors hover:bg-primary/10 hover:text-primary"
                      aria-label="Modifica"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(videostorePost)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-mockup-text-light)] transition-colors hover:bg-[var(--color-mockup-coral)]/10 hover:text-[var(--color-mockup-coral)]"
                      aria-label="Elimina"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </ListItem>
              ))}
            </List>
          ) : (
            <div className="px-6 py-12 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Video className="h-5 w-5 text-primary" />
              </div>
              <p className="text-sm font-medium text-[var(--color-mockup-text-mid)]">
                <Translate contentKey="edocendoApp.edoVideostorePost.home.notFound" />
              </p>
            </div>
          )}
        </div>
        {videostorePosts.entities?.length > 0 && (
          <Pagination
            className="flex-shrink-0"
            count={videostorePosts.totalItems}
            page={paginationState.activePage - 1}
            rowsPerPage={paginationState.itemsPerPage}
            onPageChange={handlePagination}
            onRowsPerPageChange={handleRowsPerPage}
            rowsPerPageOptions={ITEMS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Righe per pagina:"
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VideostoreDrafts;
